// Pantry subsection — "what's at home". Sits under Food alongside
// Shopping / Recipes / Meal plan. See pantry-design handoff
// (Chat/pantry/README.md) for the full UX spec.
//
// State is server-truthful via usePantry. SSE keeps cross-device
// mutations in sync. Optimistic local writes via the hook's update()
// so qty edits, lowStock toggles, etc. feel instant.

import { useMemo, useState } from "preact/hooks";
import { Icon, type IconName } from "../../icons";
import { usePantry } from "../../lib/use-pantry";
import { confirm } from "../../components/confirm";
import { QueuedDot } from "../../components/queued-dot";
import type { PantryItem, PantryCategory } from "../../lib/rpc";
import { SeasoningsManager } from "./seasonings-manager";

const CATEGORIES: PantryCategory[] = [
  "Produce", "Meat & seafood", "Dairy & fridge", "Bread & bakery",
  "Pasta & grains", "Cans", "Sauces & jars", "Oils & vinegars",
  "Spices & dry", "Snacks", "Beverages", "Frozen", "Other",
];

// Per-category accent dot color. Keyed by category name; unmapped → neutral.
const CATEGORY_COLORS: Record<string, string> = {
  "Produce":         "#5fb85f",
  "Meat & seafood":  "#e0607a",
  "Dairy & fridge":  "#e8a13d",
  "Bread & bakery":  "#c79049",
  "Pasta & grains":  "#d8b14a",
  "Cans":            "#e06a5a",
  "Sauces & jars":   "#d96bb0",
  "Oils & vinegars": "#d4c44a",
  "Spices & dry":    "#e0685e",
  "Snacks":          "#7a8cff",
  "Beverages":       "#4fb0e0",
  "Frozen":          "#48b6d6",
  "Other":           "#8a8f98",
};

type Filter = "all" | "low" | "expiring";

interface PantryProps {
  notify: (msg: string) => void;
}

export function Pantry({ notify }: PantryProps) {
  const p = usePantry();
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [seasoningsOpen, setSeasoningsOpen] = useState(false);

  const today = useMemo(() => new Date(), []);
  const todayIso = today.toISOString().slice(0, 10);

  // Derived smart-card buckets.
  const { lowStock, expSoon, expired } = useMemo(() => {
    const lowS: PantryItem[] = [];
    const soon: PantryItem[] = [];
    const exp: PantryItem[] = [];
    for (const it of p.items) {
      if (it.lowStock) lowS.push(it);
      if (it.expires) {
        if (it.expires < todayIso) exp.push(it);
        else {
          const d = (new Date(it.expires).getTime() - today.getTime()) / 86400000;
          if (d <= 14) soon.push(it);
        }
      }
    }
    soon.sort((a, b) => (a.expires || "").localeCompare(b.expires || ""));
    return { lowStock: lowS, expSoon: soon, expired: exp };
  }, [p.items, todayIso, today]);

  // Filtered + grouped main list.
  const matchesSearch = (i: PantryItem) =>
    !search.trim() ||
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    i.category.toLowerCase().includes(search.toLowerCase());

  const filtered = p.items.filter((i) => {
    if (!matchesSearch(i)) return false;
    if (filter === "low") return i.lowStock;
    if (filter === "expiring") return expSoon.includes(i) || expired.includes(i);
    return true;
  });

  const grouped = useMemo<Record<string, PantryItem[]>>(() => {
    const m: Record<string, PantryItem[]> = {};
    for (const i of filtered) {
      (m[i.category] ||= []).push(i);
    }
    return m;
  }, [filtered]);
  const orderedCats = Object.keys(grouped).sort((a, b) => {
    const ai = CATEGORIES.indexOf(a as PantryCategory);
    const bi = CATEGORIES.indexOf(b as PantryCategory);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const toggleCollapse = (cat: string) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });

  const onAdd = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setDraft("");
    const item = await p.add({ name: trimmed });
    if (item) {
      notify(`"${item.name}" added to pantry`);
    }
  };

  const onToss = async (item: PantryItem) => {
    const ok = await confirm({
      title: `Toss "${item.name}"?`,
      message: "Removes it from your pantry. You can always re-add it later.",
      confirmLabel: "Toss",
      danger: true,
    });
    if (!ok) return;
    await p.remove(item.id);
    notify(`Tossed "${item.name}"`);
  };

  return (
    <div className="pantry-shell">
      {/* Toolbar */}
      <div className="pantry-toolbar">
        <div className="pantry-stats">
          <span className="eyebrow">Pantry</span>
          <span className="pantry-stats-line">
            {p.items.length} items
            {lowStock.length > 0 && <> · {lowStock.length} low</>}
            {expSoon.length > 0 && <> · {expSoon.length} expiring</>}
            {expired.length > 0 && <> · {expired.length} expired</>}
          </span>
        </div>
        <div className="pantry-toolbar-actions">
          <div className="pantry-search">
            <Icon name="search" size={14} />
            <input
              placeholder="Search pantry…"
              value={search}
              onInput={(e) => setSearch((e.currentTarget as HTMLInputElement).value)}
            />
          </div>
          <button
            className="btn pantry-seasonings-btn"
            type="button"
            onClick={() => setSeasoningsOpen(true)}
            title="Manage the staples you keep on hand — seasonings, oils, flour, sugar"
          >
            <Icon name="sparkle" size={14} />
            <span>Staples</span>
          </button>
        </div>
      </div>

      <SeasoningsManager
        open={seasoningsOpen}
        onClose={() => setSeasoningsOpen(false)}
        notify={notify}
      />

      {/* Smart strip — combined Use soon / Expired (low-stock now lives
          in the grid via per-card badge + amber row highlight). */}
      {(expSoon.length > 0 || expired.length > 0) && (
        <div className="pantry-smart">
          <SmartCard
            icon="clock" tone="coral"
            title="Use soon"
            sub={`${expired.length + expSoon.length} item${expired.length + expSoon.length === 1 ? "" : "s"}`}
          >
            {expired.map((it) => (
              <SmartRow key={it.id} item={it}
                meta="past expiry"
                action="Toss"
                onAction={async () => {
                  await p.remove(it.id);
                  notify(`Tossed "${it.name}"`);
                }} />
            ))}
            {expSoon.map((it) => (
              <SmartRow key={it.id} item={it}
                meta={daysUntil(it.expires!, today)}
                action="Use up"
                onAction={() => notify(`Find recipes using "${it.name}" — coming soon`)} />
            ))}
          </SmartCard>
        </div>
      )}

      {/* Filter chips */}
      <div className="pantry-filters">
        {([
          { id: "all" as const,      label: `Everything · ${p.items.length}` },
          { id: "low" as const,      label: `Running low · ${lowStock.length}` },
          { id: "expiring" as const, label: `Expiring · ${expSoon.length + expired.length}` },
        ]).map((f) => (
          <button
            key={f.id}
            className="chip"
            data-active={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Add bar */}
      <div className="shop-add">
        <Icon name="plus" size={16} />
        <input
          placeholder="Add to pantry…  (e.g. 'tahini' or 'frozen dumplings — 1 bag')"
          value={draft}
          onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === "Enter") onAdd(); }}
        />
        <span className="kbd">⏎</span>
      </div>

      {/* Grouped items — masonry card grid */}
      <div className="pantry-grid">
        {orderedCats.map((cat) => {
          const rows = grouped[cat];
          const lowCount = rows.filter((r) => r.lowStock).length;
          return (
            <Group
              key={cat}
              label={cat}
              color={CATEGORY_COLORS[cat] || CATEGORY_COLORS["Other"]}
              count={rows.length}
              lowCount={lowCount}
              collapsed={collapsed.has(cat)}
              onToggle={() => toggleCollapse(cat)}
            >
              {rows.map((it) => (
                <Row
                  key={it.id} item={it} today={today}
                  onUpdate={(patch) => p.update(it.id, patch)}
                  onRemove={() => onToss(it)}
                  onToggleLow={() => p.toggleLowStock(it.id)}
                />
              ))}
            </Group>
          );
        })}
        {filtered.length === 0 && !p.loading && (
          <div className="pantry-empty">
            <Icon name="database" size={32} />
            <div className="pantry-empty-line">
              {search ? `Nothing matching "${search}".` : "Empty cupboard."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Smart card ───────────────────────────────────────────────── */

function SmartCard({
  icon, tone, title, sub, children,
}: {
  icon: IconName; tone: "amber" | "cyan" | "coral";
  title: string; sub: string; children: any;
}) {
  return (
    <div className="pantry-smart-card" data-tone={tone}>
      <div className="pantry-smart-hd">
        <span className="pantry-smart-ico"><Icon name={icon} size={13} /></span>
        <div>
          <div className="pantry-smart-title">{title}</div>
          <div className="pantry-smart-sub">{sub}</div>
        </div>
      </div>
      <div className="pantry-smart-body">{children}</div>
    </div>
  );
}

function SmartRow({
  item, meta, action, onAction,
}: {
  item: PantryItem; meta?: string;
  action: string; onAction: () => void;
}) {
  return (
    <div className="pantry-smart-row">
      <span className="pantry-smart-name">{item.name}</span>
      <span className="pantry-smart-meta">{meta || item.qty}</span>
      <button className="mini-btn" onClick={onAction}>{action}</button>
    </div>
  );
}

/* ─── Collapsible category group ───────────────────────────────── */

function Group({
  label, color, count, lowCount, collapsed, onToggle, children,
}: {
  label: string; color: string; count: number; lowCount: number;
  collapsed: boolean; onToggle: () => void; children: any;
}) {
  return (
    <div className="pantry-card" data-collapsed={collapsed}>
      <button className="pantry-card-hd" onClick={onToggle} aria-expanded={!collapsed} type="button">
        <span className="pantry-card-dot" style={{ background: color }} aria-hidden />
        <span className="pantry-card-label">{label}</span>
        {lowCount > 0 && <span className="pantry-card-low">{lowCount} low</span>}
        <span className="pantry-card-count">{count}</span>
        <span className="pantry-card-chev" aria-hidden>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>
      <div className="pantry-card-body">
        <div className="pantry-items">{children}</div>
      </div>
    </div>
  );
}

/* ─── Shared field editors (used inline on desktop + in the mobile
 *     expander added in a later task) ───────────────────────────────── */

function CategoryPicker({ value, onChange }: {
  value: PantryCategory; onChange: (c: PantryCategory) => void;
}) {
  return (
    <select
      className="pantry-cat"
      value={value}
      onChange={(e) => onChange((e.currentTarget as HTMLSelectElement).value as PantryCategory)}
      onClick={(e) => e.stopPropagation()}
      aria-label="Category"
    >
      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );
}

/* ─── Pantry item row ──────────────────────────────────────────── */

function Row({
  item, today, onUpdate, onRemove, onToggleLow,
}: {
  item: PantryItem; today: Date;
  onUpdate: (patch: Partial<PantryItem>) => void;
  onRemove: () => void;
  onToggleLow: () => void;
}) {
  const [editingName, setEditingName] = useState(false);
  const [draft, setDraft] = useState(item.qty);
  const [expDraft, setExpDraft] = useState(item.expires || "");
  const [nameDraft, setNameDraft] = useState(item.name);
  const [expanded, setExpanded] = useState(false);
  const queued = !!(item as any)._queued;

  const days = item.expires ? Math.floor((new Date(item.expires).getTime() - today.getTime()) / 86400000) : null;
  const expState: "ok" | "soon" | "expired" | null =
    days == null ? null
    : days < 0 ? "expired"
    : days <= 7 ? "soon"
    : "ok";

  const commitQty = () => {
    const t = draft.trim();
    if (!t || t === item.qty) {
      setDraft(item.qty);
      return;
    }
    onUpdate({ qty: t });
  };

  // Commit a name edit. Empty name is invalid server-side (the store
  // throws "name required"), so we revert silently rather than fire an
  // RPC we know will fail. Trim before compare so a no-op edit ("milk"
  // → " milk ") doesn't roundtrip.
  const commitName = () => {
    setEditingName(false);
    const t = nameDraft.trim();
    if (!t || t === item.name) {
      setNameDraft(item.name);
      return;
    }
    onUpdate({ name: t });
  };

  // Commit the expiry edit. Empty string = explicit "no expiry"
  // (server stores null). Validates the ISO format before sending so
  // a malformed value doesn't round-trip an RPC just to be rejected.
  //
  // Accepts an explicit value arg so callers can pass `e.currentTarget.value`
  // — avoids a race where blur fires before Preact has committed the
  // state update from the input event (synthetic event sequencing).
  const commitExp = (override?: string) => {
    const raw = (override ?? expDraft).trim();
    if (raw === (item.expires || "")) return;  // no change
    if (raw === "") {
      onUpdate({ expires: null });
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      setExpDraft(item.expires || "");  // bad input — revert silently
      return;
    }
    onUpdate({ expires: raw });
  };

  return (
    <div className="pantry-item" data-low={item.lowStock} data-exp={expState} data-expanded={expanded}>
      <button
        className={`pantry-low-btn ${item.lowStock ? "on" : ""}`}
        onClick={onToggleLow}
        title={item.lowStock ? "Marked low — click to unmark" : "Mark as running low"}
        type="button"
      >
        <Icon name="warn" size={14} />
      </button>
      <div className="pantry-name-cell">
        {editingName ? (
          <input
            className="pantry-name-input"
            autoFocus
            maxLength={200}
            value={nameDraft}
            onInput={(e) => setNameDraft((e.currentTarget as HTMLInputElement).value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitName(); }
              else if (e.key === "Escape") { e.preventDefault(); setEditingName(false); setNameDraft(item.name); }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          // Two-element layout: the name reads as plain text (so a user
          // who taps the row trying to scroll doesn't accidentally drop
          // into edit mode), and a small pencil icon to its right is
          // the actual edit trigger. Pencil is always faintly visible
          // on mobile (no hover state to rely on), darkens on hover for
          // desktop discoverability.
          <span className="pantry-name">
            <span className="pantry-name-text">{item.name}</span>
            <QueuedDot queued={queued} />
            <button
              className="pantry-name-edit-btn"
              onClick={() => { setNameDraft(item.name); setEditingName(true); }}
              title="Rename"
              aria-label="Rename"
              type="button"
            >
              <Icon name="edit" size={14} />
            </button>
          </span>
        )}
        {/* At-a-glance meta line — read-only. All editing happens in the
           expander (the chevron) so the name above can take the full row
           width without competing with inline controls (no truncation). */}
        <div className="pantry-meta">
          <span className="pantry-meta-qty">{item.qty}</span>
          {item.location && <span className="pantry-meta-loc">{item.location}</span>}
          {item.expires && (
            <span className="pantry-meta-exp" data-state={expState}>{formatRelativeExpiry(days!)}</span>
          )}
        </div>
      </div>
      <button
        className="pantry-row-chev"
        onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse" : "Expand"}
        type="button"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="m9 18 6-6-6-6" />
        </svg>
      </button>
      {expanded && (
        <div className="pantry-row-detail" onClick={(e) => e.stopPropagation()}>
          <label className="pantry-detail-field">
            <span>Qty</span>
            <input
              value={draft}
              onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
              onBlur={commitQty}
              onKeyDown={(e) => { if (e.key === "Enter") commitQty(); }}
            />
          </label>
          <label className="pantry-detail-field">
            <span>Expires</span>
            <input
              type="date"
              value={expDraft}
              onInput={(e) => setExpDraft((e.currentTarget as HTMLInputElement).value)}
              onChange={(e) => commitExp((e.currentTarget as HTMLInputElement).value)}
              onBlur={(e) => commitExp((e.currentTarget as HTMLInputElement).value)}
              style={{ colorScheme: "dark" }}
            />
          </label>
          <label className="pantry-detail-field">
            <span>Category</span>
            <CategoryPicker value={item.category} onChange={(c) => onUpdate({ category: c })} />
          </label>
          <div className="pantry-detail-actions">
            <button type="button" className="mini-btn" onClick={onToggleLow}>
              {item.lowStock ? "Unmark low" : "Mark low"}
            </button>
            <button type="button" className="mini-btn pantry-detail-toss" onClick={onRemove}>
              Toss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── helpers ──────────────────────────────────────────────────── */

function daysUntil(iso: string, today: Date): string {
  const t = new Date(iso).getTime();
  const d = Math.floor((t - today.getTime()) / 86400000);
  if (d < 0) return `${-d}d ago`;
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  return `${d}d left`;
}

function formatRelativeExpiry(days: number): string {
  if (days < 0) return `${-days}d ago`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.round(days / 7)}w`;
  return `${Math.round(days / 30)}mo`;
}
